import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useInsertionEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import type {
  FlatList as RNFlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  View as RNView,
  ViewProps,
} from "react-native";
import { Platform, View } from "react-native";

import FlatList from "$src/core/components/atoms/FlatList/FlatList";
import EPGChannelPage from "$src/core/components/organisms/EPGChannelGrid/EPGChannelPage/EPGChannelPage";
import { useEPGChannelGridLayout } from "$src/core/hooks/use-epg-channel-grid-layout";
import useEpgLiveClockMs from "$src/core/hooks/use-epg-live-clock-ms";
import findClosestProgramLayoutIndexToInstant from "$src/core/utils/epg/find-closest-program-layout-index-to-instant";
import getChannelPageLayout from "$src/core/utils/epg/get-channel-page-layout";
import getProgramListRemountKey from "$src/core/utils/epg/get-program-list-remount-key";
import getVisibleChannelIndex from "$src/core/utils/epg/get-visible-channel-index";
import isLiveClockEnabled from "$src/core/utils/epg/is-live-clock-enabled";
import programTimeRangeMs from "$src/core/utils/epg/program-time-range-ms";

import $ from "./EPGChannelGrid.styles";
import type { EPGChannelGridProps, EPGChannelWithLayouts } from "./types";

const EPGChannelGrid = ({
  ref,
  channels,
  selectedChannelIndex,
  highlightLivePrograms = false,
  onVisibleChannelChange,
  timelineScrollAnchor,
  onTVProgramsFocus,
  onTVProgramNavigateToDetail,
  onTVEpgProgramFocus,
}: EPGChannelGridProps) => {
  const {
    handleContainerLayout,
    isPagedLayout,
    columnWidth,
    pageWidth,
    gridHeight,
    compactRowHeight,
    liveRowHeight,
    pageGutter,
  } = useEPGChannelGridLayout();
  const nowMs = useEpgLiveClockMs(
    isLiveClockEnabled({ highlightLivePrograms, timelineScrollAnchor }),
  );
  const programListRemountKey = useMemo(
    () =>
      getProgramListRemountKey({
        timelineScrollAnchor,
      }),
    [timelineScrollAnchor],
  );

  const channelsRef = useRef(channels);
  const selectedChannelIndexRef = useRef(selectedChannelIndex);

  useInsertionEffect(() => {
    channelsRef.current = channels;
    selectedChannelIndexRef.current = selectedChannelIndex;
  }, [channels, selectedChannelIndex]);

  const pagedListRef = useRef<RNFlatList<EPGChannelWithLayouts> | null>(null);
  const multiListRef = useRef<RNFlatList<EPGChannelWithLayouts> | null>(null);
  const lastVisibleChannelIdRef = useRef<string | null>(null);

  const programRefs = useRef(new Map<string, RNView>());
  const focusedPositionRef = useRef<{
    channelIndex: number;
    programIndex: number;
  } | null>(null);

  const scrollToChannelIndex = useCallback(
    (index: number) => {
      if (index < 0 || index >= channels.length || pageWidth <= 0) {
        return;
      }

      requestAnimationFrame(() => {
        if (isPagedLayout) {
          pagedListRef.current?.scrollToIndex({ index, animated: true });
          return;
        }

        multiListRef.current?.scrollToIndex({ index, animated: false });
      });
    },
    [channels.length, isPagedLayout, pageWidth],
  );

  const registerProgramRef = useCallback(
    (channelId: string, entryId: string, node: RNView | null) => {
      const key = `${channelId}::${entryId}`;
      if (node) {
        programRefs.current.set(key, node);
      } else {
        programRefs.current.delete(key);
      }
    },
    [],
  );

  const focusRegisteredNode = useCallback(
    (channelId: string, entryId: string) => {
      const node = programRefs.current.get(`${channelId}::${entryId}`);
      if (!node) {
        return false;
      }
      // Web View nodes expose focus(); native Views do not.
      (node as unknown as { focus?: () => void }).focus?.();
      return true;
    },
    [],
  );

  const focusProgramAt = useCallback(
    (channelIndex: number, programIndex: number) => {
      const channel = channels[channelIndex];
      const layout = channel.programLayouts[programIndex];

      if (focusRegisteredNode(channel.id, layout.entry.id)) {
        focusedPositionRef.current = { channelIndex, programIndex };
        return true;
      }

      scrollToChannelIndex(channelIndex);
      requestAnimationFrame(() => {
        if (focusRegisteredNode(channel.id, layout.entry.id)) {
          focusedPositionRef.current = { channelIndex, programIndex };
        }
      });
      return true;
    },
    [channels, focusRegisteredNode, scrollToChannelIndex],
  );

  const focusNeighbourChannel = useCallback(
    (
      position: { channelIndex: number; programIndex: number },
      delta: -1 | 1,
    ) => {
      const targetChannelIndex = position.channelIndex + delta;
      const target = channels[targetChannelIndex];
      const current =
        channels[position.channelIndex].programLayouts[position.programIndex];
      const range = programTimeRangeMs(current.entry);

      const targetProgramIndex = range
        ? findClosestProgramLayoutIndexToInstant(
            target.programLayouts,
            range.start,
          )
        : Math.min(
            position.programIndex,
            Math.max(0, target.programLayouts.length - 1),
          );

      return focusProgramAt(targetChannelIndex, targetProgramIndex);
    },
    [channels, focusProgramAt],
  );

  const handleProgramFocus = useCallback(
    (channelId: string, entryId: string) => {
      const channelIndex = channels.findIndex((c) => c.id === channelId);
      if (channelIndex < 0) {
        return;
      }
      const programIndex = channels[channelIndex].programLayouts.findIndex(
        (row) => row.entry.id === entryId,
      );
      if (programIndex < 0) {
        return;
      }
      focusedPositionRef.current = { channelIndex, programIndex };
    },
    [channels],
  );

  const handleKeyDown = useCallback(
    (event: {
      nativeEvent?: { key?: string };
      key?: string;
      preventDefault?: () => void;
    }) => {
      const key = event.nativeEvent?.key ?? event.key;
      const position = focusedPositionRef.current;
      if (!key || !position) {
        return;
      }

      let handled = false;
      if (key === "ArrowUp") {
        handled = focusProgramAt(
          position.channelIndex,
          position.programIndex - 1,
        );
      } else if (key === "ArrowDown") {
        handled = focusProgramAt(
          position.channelIndex,
          position.programIndex + 1,
        );
      } else if (key === "ArrowLeft") {
        handled = focusNeighbourChannel(position, -1);
      } else if (key === "ArrowRight") {
        handled = focusNeighbourChannel(position, 1);
      }

      if (handled) {
        event.preventDefault?.();
      }
    },
    [focusNeighbourChannel, focusProgramAt],
  );

  const focusProgram = useCallback(
    ({ channelId, entryId }: { channelId: string; entryId: string }) => {
      const channelIndex = channels.findIndex((c) => c.id === channelId);
      if (channelIndex < 0) {
        return;
      }
      const programIndex = channels[channelIndex].programLayouts.findIndex(
        (row) => row.entry.id === entryId,
      );
      if (programIndex < 0) {
        return;
      }
      focusProgramAt(channelIndex, programIndex);
    },
    [channels, focusProgramAt],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const focusTimelineProgramForChannel = useCallback((_channelId: string) => {
    /* TV-only; no-op on phone/web */
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      scrollToChannelIndex,
      focusProgram,
      focusTimelineProgramForChannel,
    }),
    [focusProgram, focusTimelineProgramForChannel, scrollToChannelIndex],
  );

  useInsertionEffect(() => {
    channelsRef.current = channels;
  }, [channels]);

  useLayoutEffect(() => {
    if (!timelineScrollAnchor) {
      return;
    }

    const currentChannels = channelsRef.current;

    if (currentChannels.length === 0) {
      return;
    }

    const safeSelectedIndex = Math.max(
      0,
      Math.min(selectedChannelIndexRef.current, currentChannels.length - 1),
    );
    const selectedChannel = currentChannels[safeSelectedIndex];

    requestAnimationFrame(() => {
      if (isPagedLayout) {
        pagedListRef.current?.scrollToIndex({
          index: safeSelectedIndex,
          animated: false,
        });
      }

      multiListRef.current?.scrollToIndex({
        index: safeSelectedIndex,
        animated: false,
      });
    });

    onVisibleChannelChange?.(selectedChannel.id);
    // No nowMs/highlightLivePrograms: unread here, and the 60s clock re-ran this restore.
  }, [
    channelsRef,
    isPagedLayout,
    onVisibleChannelChange,
    programListRemountKey,
    selectedChannelIndexRef,
    timelineScrollAnchor,
  ]);

  useEffect(() => {
    scrollToChannelIndex(selectedChannelIndex);
  }, [scrollToChannelIndex, selectedChannelIndex]);

  const syncVisibleChannel = useCallback(
    (offsetX: number) => {
      if (pageWidth <= 0) return;

      const visibleIndex = getVisibleChannelIndex({
        offsetX,
        scrollUnit: pageWidth,
      });

      if (visibleIndex < 0 || visibleIndex >= channels.length) return;

      const visibleChannelId = channels[visibleIndex].id;

      if (lastVisibleChannelIdRef.current === visibleChannelId) return;

      lastVisibleChannelIdRef.current = visibleChannelId;
      onVisibleChannelChange?.(visibleChannelId);
    },
    [channels, onVisibleChannelChange, pageWidth],
  );

  const handlePagedMomentumScrollEnd = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!isPagedLayout) return;
      syncVisibleChannel(e.nativeEvent.contentOffset.x);
    },
    [isPagedLayout, syncVisibleChannel],
  );

  const handleTVProgramsZoneEnter = useCallback(() => {
    onTVProgramsFocus?.();
  }, [onTVProgramsFocus]);

  const renderChannelPage = useCallback(
    ({ item }: { item: EPGChannelWithLayouts }) => (
      <EPGChannelPage
        channel={item}
        columnWidth={columnWidth}
        outerHeight={gridHeight}
        pageWidth={pageWidth}
        pageGutter={pageGutter}
        preferredFocusChannelId={null}
        preferredFocusProgramIndex={null}
        compactRowHeight={compactRowHeight}
        liveRowHeight={liveRowHeight}
        timelineScrollAnchor={timelineScrollAnchor}
        programListRemountKey={programListRemountKey}
        highlightLivePrograms={highlightLivePrograms}
        nowMs={nowMs}
        onTVProgramsFocus={handleTVProgramsZoneEnter}
        onTVProgramNavigateToDetail={onTVProgramNavigateToDetail}
        onTVEpgProgramFocus={onTVEpgProgramFocus}
        registerProgramRef={registerProgramRef}
        onProgramFocus={handleProgramFocus}
      />
    ),
    [
      columnWidth,
      compactRowHeight,
      liveRowHeight,
      gridHeight,
      handleProgramFocus,
      handleTVProgramsZoneEnter,
      highlightLivePrograms,
      nowMs,
      onTVEpgProgramFocus,
      onTVProgramNavigateToDetail,
      pageGutter,
      pageWidth,
      programListRemountKey,
      registerProgramRef,
      timelineScrollAnchor,
    ],
  );

  const keyExtractor = useCallback(
    (item: EPGChannelWithLayouts) => item.id,
    [],
  );

  const getItemLayout = useMemo(
    () => getChannelPageLayout(pageWidth),
    [pageWidth],
  );

  if (channels.length === 0) return null;

  const rootWebProps =
    Platform.OS === "web"
      ? ({ onKeyDown: handleKeyDown } as unknown as ViewProps)
      : null;

  return (
    <View style={$.root} {...rootWebProps}>
      <View style={$.root} onLayout={handleContainerLayout}>
        {isPagedLayout ? (
          <FlatList
            accessibilityRole="list"
            ref={pagedListRef}
            data={channels}
            horizontal
            pagingEnabled
            decelerationRate="fast"
            renderItem={renderChannelPage}
            keyExtractor={keyExtractor}
            getItemLayout={getItemLayout}
            onMomentumScrollEnd={handlePagedMomentumScrollEnd}
            showsHorizontalScrollIndicator={false}
          />
        ) : (
          <FlatList
            accessibilityRole="list"
            ref={multiListRef}
            data={channels}
            horizontal
            renderItem={renderChannelPage}
            keyExtractor={keyExtractor}
            getItemLayout={getItemLayout}
            showsHorizontalScrollIndicator={false}
            initialNumToRender={3}
            maxToRenderPerBatch={3}
            windowSize={7}
            removeClippedSubviews={Platform.OS !== "web"}
            contentContainerStyle={$.channelPageRow}
          />
        )}
      </View>
    </View>
  );
};

export default EPGChannelGrid;
